import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  // State
  const userPreference = ref<'light' | 'dark' | 'system'>('system')
  const systemPreference = ref<'light' | 'dark'>('light')

  // Computed
  const isDarkMode = computed(() => {
    if (userPreference.value === 'system') {
      return systemPreference.value === 'dark'
    }
    return userPreference.value === 'dark'
  })

  // Actions
  const initializeTheme = async () => {
    // Load saved preference
    const saved = localStorage.getItem('theme-preference')
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      userPreference.value = saved as 'light' | 'dark' | 'system'
    }

    // Detect system preference
    detectSystemPreference()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', detectSystemPreference)

    // Apply initial theme
    applyTheme()
  }

  const setTheme = (theme: 'light' | 'dark' | 'system') => {
    userPreference.value = theme
    localStorage.setItem('theme-preference', theme)
    applyTheme()
  }

  const toggleTheme = () => {
    const currentEffectiveTheme = isDarkMode.value ? 'dark' : 'light'
    const newTheme = currentEffectiveTheme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }

  const detectSystemPreference = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPreference.value = mediaQuery.matches ? 'dark' : 'light'
  }

  const applyTheme = () => {
    const root = document.documentElement
    
    if (isDarkMode.value) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }

  return {
    // State
    userPreference,
    systemPreference,
    
    // Computed
    isDarkMode,
    
    // Actions
    initializeTheme,
    setTheme,
    toggleTheme
  }
})
